import { useMemo, useState, useEffect } from 'react';
import { Button, Input, Label } from 'reactstrap';
import { useTranslation, Trans } from 'react-i18next';

import { createChangeHandler } from 'services/formUtils';
import { SubmitButton } from 'components';
import './CustomLogin.css';

export interface ISetPasswordProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  defaultEmail?: string;
  onEmailChange?: (newValue: string) => void;
  onRestart?: () => void;
  onRequestResetPasswordToken: () => Promise<void>;
}

const PasswordLogin = (props: ISetPasswordProps) => {
  const { t } = useTranslation('translation');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (email === '' && props.defaultEmail !== email && props.defaultEmail) {
      setEmail(props.defaultEmail);
    }
  }, [props.defaultEmail]);

  useEffect(() => {
    props.onEmailChange && props.onEmailChange(email);
  }, [email]);

  const onSubmit = () => {
    if (!canBeSubmitted) return;
    setIsSubmitting(true);
    props
      .onSubmit(email, password)
      .then(
        () => {
          setIsSubmitting(false);
        },
        () => {
          setIsSubmitting(false);
        }
      )
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const canBeSubmitted = useMemo(() => {
    var canBeSubmitted: boolean = password.length > 0 && email.length > 0 && !isSubmitting;
    return canBeSubmitted || true;
  }, [isSubmitting, password, email]);

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Label>
          {t('login.LABEL_EMAIL')}{' '}
          <Input
            name="email"
            autoComplete="username"
            placeholder={t('login.PROMPT_FOR_EMAIL')}
            value={email}
            type="text"
            onChange={createChangeHandler(setEmail)}
            readOnly
          />
        </Label>
        <Label>
          {t('login.LABEL_PASSWORD')}{' '}
          <Input
            name="password"
            autoComplete="current-password"
            placeholder={t('login.PROMPT_FOR_PASSWORD')}
            type="password"
            value={password}
            onChange={createChangeHandler(setPassword)}
          />
        </Label>
        <span className="buttons">
          {props.onRestart && (
            <Button type="button" onClick={props.onRestart}>
              {t('login.SUBMIT_BUTTON_RESTART')}
            </Button>
          )}
          <SubmitButton
            type="submit"
            submitting={isSubmitting ? true : undefined}
            disabled={!canBeSubmitted}
            label={t('login.SUBMIT_BUTTON_REQUEST_ONE_USE_PASSWORD')}
            activelabel={t('login.SUBMIT_BUTTON_SUBMITTING')}
            className="btn-primary"
          />
        </span>
      </form>
      <span className="resetPasswordPrompt">
        <Trans i18nKey="login.INFO_REQUEST_NEW_PASSWORD_RESET_LINK" ns="translation">
          <a
            href="#"
            onClick={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              setPassword('');
              await props.onRequestResetPasswordToken();
            }}
          ></a>
        </Trans>
      </span>
    </>
  );
};
export default PasswordLogin;
